import { useState } from "react";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { Table, PageHeader, Card, Button } from "@cofano/cck";

import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useFrontendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import { Language, usePermission, DataFormat } from "imports/Utils";

import LabelCreateEditDialog from "./components/dialogs/LabelCreateEditDialog";
import LabelTypeSelect from "./components/selects/LabelTypeSelect";

import { GetLabelsDocument, GetLabelsQuery } from "__generated__/view/labels/graphql/queries.generated";
import { DeleteLabelDocument, DeleteLabelMutation } from "__generated__/view/labels/graphql/mutations.generated";

import { ChildPropsFromArray } from "types/commons";

type LabelFromQuery = ChildPropsFromArray<GetLabelsQuery, "labels">

const Labels = () => {

	const confirm = useConfirm();
	const { isReadOnly } = usePermission();

	const { data, refetch, loading } = useQuery<GetLabelsQuery>(GetLabelsDocument);
	const [ deleteLabel ] = useMutation<DeleteLabelMutation>(DeleteLabelDocument);

	const [ dialog, setDialog ] = useState<{ open: boolean; label: LabelFromQuery | null }>({
		open: false,
		label: null,
	});

	const { tableProps, refetchAfterDelete } = useFrontendTable({
		id: TableId.Labels,
		content: data?.labels ?? [],
		onCustomFilter: (property: string) => {
			if (property === "type") {
				return (field: unknown, value: unknown) => field === value;
			}
		},
	});

	const onDeleteLabel = (id: number) => {
		deleteLabel({
			variables: {
				id: id,
			},
		}).then(() => {
			refetchAfterDelete();
			refetch();
		});
	};

	return (
		<div>
			<PageHeader>
				<PageHeader.Title>
					{Language("LABELS")}
					<Helmet title={intl.formatMessage({ id: "LABELS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					{!isReadOnly && (
						<Button onClick={() => setDialog({ open: true, label: null })}>
							{Language("ADD_BUTTON")}
						</Button>
					)}
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					contentLoading={loading}
					columns={[ {
						id: "text",
						title: intl.formatMessage({ id: "DESCRIPTION" }),
						sortable: true,
						filterComponent: "TEXT",
					}, {
						id: "color",
						title: intl.formatMessage({ id: "COLOR" }),
						render: (row: LabelFromQuery) => DataFormat.color(row.color),
					}, {
						id: "type",
						title: intl.formatMessage({ id: "TYPE" }),
						sortable: true,
						render: (row: LabelFromQuery) => Language(row.type),
						filterComponent: (value, onChange) => (
							<LabelTypeSelect
								label={intl.formatMessage({ id: "TYPE" })}
								value={value}
								onChange={onChange} />
						),
					} ]}
					rowActions={isReadOnly ? undefined : [ {
						icon: <EditIcon />,
						onClick: (row: LabelFromQuery) => setDialog({ open: true, label: row }),
					}, {
						icon: <DeleteIcon />,
						onClick: (row: LabelFromQuery) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								onDeleteLabel(row.id);
							});
						},
					} ]}  />
			</Card>

			<LabelCreateEditDialog
				open={dialog.open}
				label={dialog.label}
				onClose={() => setDialog({ open: false, label: null })}
				onSave={() => refetch()} />
		</div>
	);
};

export default Labels;
